import React, { useState } from 'react'
import moment from 'moment'
import styled from 'styled-components'

import { media } from 'styles/media'
import Avatar from 'components/display/Avatar'
import LazyImage from 'components/display/LazyImage'
import ReviewContent from './ReviewContent'
import ReviewMoments from './ReviewMoments'

export const CONTENT_TRUNCATE = 300
export const MOMENTS_TRUNCATE = 6

interface CondensedProp {
  condensed?: boolean
}

export const ReviewWrapper = styled.div<CondensedProp>`
  display: grid;
  gap: 16px;
  grid-template-columns: 64px 1fr;
  align-items: center;
  min-height: 176px;
  padding: 24px;
  background-color: ${({ theme }) => theme?.['-ds-neutral-200']?.value};
  border-radius: 8px;

  ${media.lg<CondensedProp>`
    align-items: ${({ condensed }) => (!condensed ? 'flex-start' : 'center')};
    gap: ${({ condensed }) => (!condensed ? '16px 32px' : '16px')};
    grid-template-columns: ${({ condensed }) =>
      !condensed ? '88px 1fr' : '64px 1fr'};
  `}
`

const AvatarIcon = styled(Avatar)<CondensedProp>`
  width: 64px;
  height: 64px;

  ${media.lg<CondensedProp>`
    width: ${({ condensed }) => (!condensed ? '88px' : '64px')};
    height: ${({ condensed }) => (!condensed ? '88px' : '64px')};
    grid-row-start: ${({ condensed }) => (!condensed ? '1' : 'auto')};
    grid-row-end: ${({ condensed }) => (!condensed ? '3' : 'auto')};
  `}
`

const AuthorName = styled.h4<CondensedProp>`
  font-size: ${({ theme, condensed }) =>
    !condensed
      ? theme?.['-ds-font-size-lg']?.['font-size']?.value
      : theme?.['-ds-font-size-md']?.['font-size']?.value};
  line-height: 1.4;
  margin-bottom: ${({ condensed }) => (!condensed ? '4px' : '0')};
`

const Date = styled.div`
  color: ${({ theme }) => theme?.['-ds-neutral-600']?.value};
  font-size: ${({ theme }) =>
    theme?.['-ds-font-size-xxs']?.['font-size']?.value};
  font-weight: 500;
`

const Content = styled.div<CondensedProp>`
  grid-column-start: 1;
  grid-column-end: 3;
  letter-spacing: 0.01em;

  ${media.lg<CondensedProp>`
    grid-column-start: ${({ condensed }) => (!condensed ? '2' : '1')};
  `}
`

const ShowMoreButton = styled.button<{ open?: boolean }>`
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
  font-size: ${({ theme }) =>
    theme?.['-ds-font-size-xxs']?.['font-size']?.value};
  font-weight: 500;
  background-color: transparent;
  border: none;
  color: ${({ theme }) => theme?.['-ds-core-400'].value};
  transition: color 0.3s;
  position: relative;
  grid-column-start: 1;
  grid-column-end: 3;
  margin-top: 16px;

  &:hover,
  &:focus {
    color: ${({ theme }) => theme?.['-ds-core-300'].value};
  }

  svg {
    margin-left: 16px;
    transform: ${({ open }) => (open ? 'rotate(180deg)' : 'none')};
  }
`

type ReviewProps = {
  review: GQL_ListingReviewFragment
  condensed?: boolean
}

const Review = ({
  review: { reviewer, createdAt, content, fulfillingMoments },
  condensed,
}: ReviewProps) => {
  const [showMore, setShowMore] = useState(false)

  const hasShowMore =
    content.length > CONTENT_TRUNCATE ||
    fulfillingMoments.length > MOMENTS_TRUNCATE

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault()
    setShowMore(!showMore)
  }

  return (
    <ReviewWrapper condensed={condensed}>
      <AvatarIcon condensed={condensed}>
        <LazyImage data-src={reviewer.avatar.thumbLg} />
        <noscript>
          <LazyImage className="lazyload" src={reviewer.avatar.thumbLg} />
        </noscript>
      </AvatarIcon>

      <div>
        <AuthorName condensed={condensed}>{reviewer.firstName}</AuthorName>
        <Date>{moment(createdAt).format('D MMM YYYY')}</Date>
      </div>

      <Content condensed={condensed}>
        {content && (
          <ReviewContent
            content={content}
            showMore={showMore}
            truncateLength={CONTENT_TRUNCATE}
          />
        )}

        {fulfillingMoments && (
          <ReviewMoments
            moments={fulfillingMoments}
            showMore={showMore}
            truncateLength={MOMENTS_TRUNCATE}
          />
        )}
      </Content>

      {hasShowMore && (
        <ShowMoreButton onClick={handleClick} open={showMore}>
          Show {!showMore ? 'more' : 'less'}
          <svg width="11" height="5" viewBox="0 0 11 5" fill="none">
            <path d="M0.5 0L5.5 5L10.5 0H0.5Z" fill="currentColor" />
          </svg>
        </ShowMoreButton>
      )}
    </ReviewWrapper>
  )
}

export default Review
